import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Card,
  Row,
  Col,
  Tag,
  Space,
  Button,
  Typography,
  Descriptions,
  Tabs,
  Table,
  Alert,
  Modal,
  Form,
  Input,
  Select,
  message,
  Badge,
  Tooltip,
  Empty,
  DatePicker
} from 'antd';
import * as XLSX from 'xlsx';
import dayjs from 'dayjs';
import {
  ArrowLeftOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  WarningOutlined,
  InfoCircleOutlined,
  SettingOutlined,
  BarChartOutlined,
  HistoryOutlined,
  ThunderboltOutlined,
  SignalFilled,
  FireOutlined,
  DashboardOutlined,
  BugOutlined,
  RocketOutlined,
  FileTextOutlined,
  CopyOutlined,
  KeyOutlined,
  DownloadOutlined
} from '@ant-design/icons';
import axios from 'axios';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, Legend } from 'recharts';
import websocketService from '../../services/websocket';
import { formatTimestamp, getRelativeTime, isRecent, getCurrentUTCTimestamp, safeFormatTimestamp, safeGetRelativeTime } from '../../utils/timezone';
import { useAuth } from '../../contexts/AuthContext';
import { hasPermission } from '../../utils/rolePermissions';
import { formatArea, formatGender, formatSkinType, formatHairType } from '../../utils/sessionEnums';
import logger from '../../services/logger';

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const DeviceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const isCustomer = user?.role?.name === 'customer';

  // URL'den from parametresini al
  const searchParams = new URLSearchParams(location.search);
  const fromPage = searchParams.get('from');
  
  // State Management
  const [device, setDevice] = useState(null);
  const [activeRental, setActiveRental] = useState(null);
  const [rentalHistory, setRentalHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [telemetryData, setTelemetryData] = useState([]);
  const [errorLogs, setErrorLogs] = useState([]);
  const [errorLogsLoading, setErrorLogsLoading] = useState(false);
  const [allLogs, setAllLogs] = useState([]);
  const [allLogsLoading, setAllLogsLoading] = useState(false);
  const [sessionLogsRaw, setSessionLogsRaw] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [activeTab, setActiveTab] = useState('overview');
  const [logsLoading, setLogsLoading] = useState(false);
  const [mqttCredentials, setMqttCredentials] = useState(null);
  const [mqttCredentialsLoading, setMqttCredentialsLoading] = useState(false);
  const [otaHistory, setOtaHistory] = useState([]);
  const [otaHistoryLoading, setOtaHistoryLoading] = useState(false);

  // Telemetry filter state - default: bugün
  const [telemetryDateRange, setTelemetryDateRange] = useState([dayjs().startOf('day'), dayjs().endOf('day')]);

  // Error logs filter - default: bugün
  const [errorDateRange, setErrorDateRange] = useState([dayjs().startOf('day'), dayjs().endOf('day')]);

  // OTA filter - default: bugün
  const [otaDateRange, setOtaDateRange] = useState([dayjs().startOf('day'), dayjs().endOf('day')]);

  // All logs filter - default: bugün
  const [allLogsDateRange, setAllLogsDateRange] = useState([dayjs().startOf('day'), dayjs().endOf('day')]);

  // Sessions filter and stats states - default: bugün
  const [sessionDateRange, setSessionDateRange] = useState([dayjs().startOf('day'), dayjs().endOf('day')]);
  const [sessionStats, setSessionStats] = useState(null);
  const [sessionStatsLoading, setSessionStatsLoading] = useState(false);

  // Fetch Functions
  const fetchDeviceData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/v1/devices/${id}`);
      setDevice(response.data);

      // Aktif kiralama bilgisini al
      try {
        const rentalResponse = await axios.get(`/api/v1/rentals?device_id=${id}&active_only=true`);
        if (rentalResponse.data && rentalResponse.data.length > 0) {
          setActiveRental(rentalResponse.data[0]);
        } else {
          setActiveRental(null);
        }
        // Kiralama geçmişi (son 5) — müşteri rolünde gereksiz
        if (!isCustomer) {
          const historyResponse = await axios.get(`/api/v1/rentals/device/${id}/history`);
          setRentalHistory(Array.isArray(historyResponse.data) ? historyResponse.data.slice(0, 5) : []);
        }
      } catch (rentalError) {
        logger.debug('Kiralama bilgisi alınamadı:', rentalError);
        setActiveRental(null);
        setRentalHistory([]);
      }
    } catch (error) {
      logger.error('Device fetch error:', error);
      message.error('Cihaz bilgileri yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const fetchMqttCredentials = async () => {
    // Sadece admin ve manager görebilir
    if (!hasPermission(user?.role, 'devices.view_mqtt_credentials')) {
      return;
    }

    try {
      setMqttCredentialsLoading(true);
      const response = await axios.get(`/api/v1/devices/${id}/mqtt-credentials`);
      setMqttCredentials(response.data);
    } catch (error) {
      logger.error('MQTT credentials fetch error:', error);
      // Permission hatası sessizce yoksay
      if (error.response?.status === 403) {
        setMqttCredentials(null);
      } else if (error.response?.status !== 404) {
        message.error('MQTT bağlantı bilgileri yüklenirken hata oluştu');
      }
    } finally {
      setMqttCredentialsLoading(false);
    }
  };

  const fetchTelemetryData = async (startTime = null, endTime = null) => {
    try {
      setLogsLoading(true);
      const params = new URLSearchParams({ limit: '1000' });
      if (startTime) params.append('start_time', startTime.toISOString());
      if (endTime) params.append('end_time', endTime.toISOString());
      const response = await axios.get(`/api/v1/devices/${id}/telemetry?${params.toString()}`);
      
      // Backend nested response - flatten it
      if (response.data && response.data.telemetry_data) {
        const flattenedData = response.data.telemetry_data.map(item => ({
          timestamp: item.timestamp,
          water_temperature: item.sensors?.water_temperature,
          ambient_temperature: item.sensors?.ambient_temperature,
          signal_strength: item.sensors?.signal_strength,
          water_flow: item.sensors?.water_flow,
          operational_status: item.status?.operational_status,
          status_summary: item.status?.status_summary,
          total_shots: item.device_info?.total_shots,
          firmware_version: item.device_info?.firmware_version,
          stm32_firmware_version: item.device_info?.stm32_firmware_version
        }));
        setTelemetryData(flattenedData);
      } else {
        setTelemetryData([]);
      }
    } catch (error) {
      logger.error('Telemetry fetch error:', error);
      message.error('Telemetri verisi yüklenirken hata oluştu');
      setTelemetryData([]);
    } finally {
      setLogsLoading(false);
    }
  };

  const fetchErrorLogs = async (startTime = null, endTime = null) => {
    try {
      setErrorLogsLoading(true);
      const params = new URLSearchParams({ log_type: 'error', limit: '500' });
      if (startTime) params.append('start_time', startTime.toISOString());
      if (endTime) params.append('end_time', endTime.toISOString());
      const response = await axios.get(`/api/v1/logs/devices/${id}/logs?${params.toString()}`);
      setErrorLogs(response.data?.logs || (Array.isArray(response.data) ? response.data : []));
    } catch (error) {
      logger.error('Error logs fetch error:', error);
      setErrorLogs([]);
    } finally {
      setErrorLogsLoading(false);
    }
  };

  const fetchAllLogs = async (startTime = null, endTime = null) => {
    try {
      setAllLogsLoading(true);
      const params = new URLSearchParams({ limit: '1000' });
      if (startTime) params.append('start_time', startTime.toISOString());
      if (endTime) params.append('end_time', endTime.toISOString());
      const response = await axios.get(`/api/v1/logs/devices/${id}/logs?${params.toString()}`);
      setAllLogs(response.data?.logs || (Array.isArray(response.data) ? response.data : []));
    } catch (error) {
      logger.error('All logs fetch error:', error);
      setAllLogs([]);
    } finally {
      setAllLogsLoading(false);
    }
  };

  const fetchSessionLogs = async () => {
    try {
      setLogsLoading(true);
      // Fetch only session logs with high limit (sessions can be many)
      const response = await axios.get(`/api/v1/logs/devices/${id}/logs?log_type=session&limit=2000`);

      const logs = response.data?.logs || (Array.isArray(response.data) ? response.data : []);
      setSessionLogsRaw(logs);
    } catch (error) {
      logger.error('Session logs fetch error:', error);
      // Don't show error message if it's just missing data
      if (error.response?.status !== 404) {
        message.error('Seans logları yüklenirken hata oluştu');
      }
    } finally {
      setLogsLoading(false);
    }
  };

  const fetchOTAHistory = async () => {
    try {
      setOtaHistoryLoading(true);
      const response = await axios.get(`/api/v1/devices/${id}/ota-history`);
      setOtaHistory(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      logger.error('OTA History fetch error:', error);
      message.error('OTA geçmişi yüklenirken hata oluştu');
      setOtaHistory([]);
    } finally {
      setOtaHistoryLoading(false);
    }
  };

  const fetchSessionStats = async (startTime = null, endTime = null) => {
    try {
      setSessionStatsLoading(true);
      let url = `/api/v1/logs/devices/${id}/sessions/stats`;
      const params = new URLSearchParams();

      if (startTime) {
        params.append('start_time', startTime.toISOString());
      }
      if (endTime) {
        params.append('end_time', endTime.toISOString());
      }

      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const response = await axios.get(url);
      setSessionStats(response.data.statistics);
    } catch (error) {
      logger.error('Session stats fetch error:', error);
      message.error('Seans istatistikleri yüklenirken hata oluştu');
      setSessionStats(null);
    } finally {
      setSessionStatsLoading(false);
    }
  };

  const handleExportToExcel = () => {
    try {
      // Filter session logs based on date range (session_start_time bazlı)
      let filteredSessions = sessionLogs;

      if (sessionDateRange && sessionDateRange.length === 2) {
        const [start, end] = sessionDateRange;
        filteredSessions = sessionLogs.filter(log => {
          const sessionTime = new Date(log.session_start_time || log.timestamp);
          return sessionTime >= start.toDate() && sessionTime <= end.toDate();
        });
      }

      if (filteredSessions.length === 0) {
        message.warning('Dışa aktarılacak seans bulunamadı');
        return;
      }

      // Prepare data for Excel
      const excelData = filteredSessions.map(log => ({
        'Başlangıç': log.session_start_time ? safeFormatTimestamp(log.session_start_time) : '-',
        'Bitiş': log.session_end_time ? safeFormatTimestamp(log.session_end_time) : '-',
        'Süre (dk)': log.session_duration ? Math.floor(log.session_duration / 60) : '-',
        'Süre (sn)': log.session_duration ? log.session_duration % 60 : '-',
        'Atış': log.session_shots || '-',
        'Alan': log.session_area || '-',
        'Joule (J)': log.session_joule || '-',
        'Frekans (Hz)': log.session_hz || '-',
        'Mesaj': log.message || '-'
      }));

      // Create worksheet
      const worksheet = XLSX.utils.json_to_sheet(excelData);

      // Create workbook
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Seanslar');

      // Generate filename
      const dateStr = sessionDateRange
        ? `${sessionDateRange[0].format('YYYY-MM-DD')}_${sessionDateRange[1].format('YYYY-MM-DD')}`
        : 'tum';
      const filename = `${device?.name || 'cihaz'}_seanslar_${dateStr}.xlsx`;

      // Download
      XLSX.writeFile(workbook, filename);
      message.success(`${filteredSessions.length} seans Excel'e aktarıldı`);
    } catch (error) {
      logger.error('Excel export error:', error);
      message.error('Excel dışa aktarma hatası');
    }
  };

  const handleRetryOTA = async (otaRecord, force = false) => {
    try {
      const response = await axios.post(
        `/api/v1/firmware/ota-history/${otaRecord.id}/retry`,
        { force }
      );
      message.success(response.data.message || 'OTA tekrar gönderildi');
      fetchOTAHistory(); // Refresh history
    } catch (error) {
      logger.error('OTA Retry error:', error);
      message.error(error.response?.data?.detail || 'OTA retry başarısız oldu');
    }
  };

  const handleRetryOTAWithConfirm = (otaRecord) => {
    // Cihaz zaten hedef versiyonda mı kontrol et
    const deviceVersion = latestTelemetry?.firmware_version || device?.firmware_version;
    const targetVersion = otaRecord.firmware?.version;

    if (deviceVersion === targetVersion) {
      // Aynı versiyonda - Force update confirmation sor
      Modal.confirm({
        title: 'Zorla Güncelleme',
        content: (
          <div>
            <p>Cihaz zaten <strong>{targetVersion}</strong> versiyonunda.</p>
            <p>Aynı versiyonu tekrar yüklemek istiyor musunuz?</p>
            <p style={{ color: '#ff4d4f', fontSize: 12, marginTop: 8 }}>
              ⚠️ Bu işlem aynı firmware'i tekrar indirecek ve yükleyecektir.
            </p>
          </div>
        ),
        okText: 'Evet, Zorla Güncelle',
        cancelText: 'İptal',
        onOk: () => handleRetryOTA(otaRecord, true)  // force=true
      });
    } else {
      // Farklı versiyonlarda - Normal retry
      handleRetryOTA(otaRecord, false);  // force=false
    }
  };

  // Quick filter handlers for sessions
  const handleQuickFilter = (type) => {
    const now = dayjs();
    let range;

    switch (type) {
      case 'today':
        range = [now.startOf('day'), now.endOf('day')];
        break;
      case 'last7days':
        range = [now.subtract(7, 'days').startOf('day'), now.endOf('day')];
        break;
      case 'last30days':
        range = [now.subtract(30, 'days').startOf('day'), now.endOf('day')];
        break;
      case 'thisMonth':
        range = [now.startOf('month'), now.endOf('month')];
        break;
      case 'all':
        range = null;
        break;
      default:
        range = null;
    }

    setSessionDateRange(range);
  };

  const fetchCustomers = async () => {
    try {
      const response = await axios.get('/api/v1/customers');
      setCustomers(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      logger.error('Customers fetch error:', error);
      setCustomers([]);
    }
  };

  // WebSocket Connection
  useEffect(() => {
    fetchDeviceData();
    if (!isCustomer) {
      fetchCustomers();
      // Initial fetch'ler - tarih filtreli olanlar useEffect'lerden tetiklenecek
      fetchOTAHistory();
      fetchMqttCredentials();
    }

    const token = localStorage.getItem('token');
    if (token && websocketService) {
        websocketService.connect(token);
        
        // WebSocket subscription for real-time telemetry updates
        const unsubscribeTelemetry = websocketService.subscribe('telemetry', (data) => {
          if (data.type === 'telemetry' && data.device_id === parseInt(id)) {
            const sensors = data.data?.sensors || {};
            const status = data.data?.status || {};
            const deviceInfo = data.data?.device_info || {};

            const newEntry = {
              timestamp: data.timestamp,
              water_temperature: sensors.water_temperature,
              ambient_temperature: sensors.ambient_temperature,
              signal_strength: sensors.signal_strength,
              water_flow: sensors.water_flow,
              operational_status: status.operational_status,
              status_summary: status.status_summary,
              total_shots: deviceInfo.total_shots,
              firmware_version: deviceInfo.firmware_version,
              stm32_firmware_version: deviceInfo.stm32_firmware_version
            };

            // Backend desc sıralı: [0] = en yeni. WebSocket'ten gelen yeni veriyi başa ekle
            setTelemetryData(prev => [newEntry, ...prev]);

            // Device last_seen ve status güncelle
            setDevice(prevDevice => {
              if (prevDevice && prevDevice.id === data.device_id) {
                return {
                  ...prevDevice,
                  status: data.device_status || prevDevice.status,
                  last_seen: data.last_seen || getCurrentUTCTimestamp(),
                  operational_status: data.operational_status || prevDevice.operational_status,
                  status_summary: data.status_summary || prevDevice.status_summary
                };
              }
              return prevDevice;
            });
          }
        });

        // WebSocket subscription for device status updates
        const unsubscribeDeviceStatus = websocketService.subscribe('device_status', (data) => {
          if (data.type === 'device_status' && data.device_id === parseInt(id)) {
            setDevice(prevDevice => {
              if (prevDevice && prevDevice.id === data.device_id) {
                return {
                  ...prevDevice,
                  status: data.status,
                  last_seen: data.last_seen || getCurrentUTCTimestamp(),
                  ...(data.data?.operational_status && {
                    operational_status: data.data.operational_status
                  })
                };
              }
              return prevDevice;
            });
          }
        });

        // WebSocket subscription for OTA status updates
        const unsubscribeOTAStatus = websocketService.subscribe('ota_status', (data) => {
          if (data.type === 'ota_status' && data.device_id === parseInt(id)) {
            // OTA status güncellemesi geldi, OTA history'yi yenile
            fetchOTAHistory();

            // Eğer başarılı olduysa device firmware_version'ı da güncelle
            if (data.data?.status === 'completed') {
              setDevice(prevDevice => {
                if (prevDevice && prevDevice.id === data.device_id) {
                  return {
                    ...prevDevice,
                    firmware_version: data.data.firmware_version || prevDevice.firmware_version
                  };
                }
                return prevDevice;
              });
            }

            // Kullanıcıya bildirim göster
            if (data.data?.status === 'completed') {
              message.success(`OTA güncelleme tamamlandı: ${data.data.firmware_version}`);
            } else if (data.data?.status === 'failed') {
              message.error(`OTA güncelleme başarısız oldu: ${data.data.error_message || 'Bilinmeyen hata'}`);
            }
          }
        });

        return () => {
          unsubscribeTelemetry();
          unsubscribeDeviceStatus();
          unsubscribeOTAStatus();
        };
    }
  }, [id]);

  // Update telemetry data when date range changes
  useEffect(() => {
    if (telemetryDateRange && telemetryDateRange.length === 2) {
      fetchTelemetryData(telemetryDateRange[0].toDate(), telemetryDateRange[1].toDate());
    } else {
      fetchTelemetryData();
    }
  }, [telemetryDateRange]);

  // Update error logs when date range changes
  useEffect(() => {
    if (isCustomer) return;
    if (errorDateRange && errorDateRange.length === 2) {
      fetchErrorLogs(errorDateRange[0].toDate(), errorDateRange[1].toDate());
    } else {
      fetchErrorLogs();
    }
  }, [errorDateRange, isCustomer]);

  // Update all logs when date range changes
  useEffect(() => {
    if (isCustomer) return;
    if (allLogsDateRange && allLogsDateRange.length === 2) {
      fetchAllLogs(allLogsDateRange[0].toDate(), allLogsDateRange[1].toDate());
    } else {
      fetchAllLogs();
    }
  }, [allLogsDateRange, isCustomer]);

  // Update session logs + stats when date range changes
  useEffect(() => {
    fetchSessionLogs();
    if (sessionDateRange && sessionDateRange.length === 2) {
      fetchSessionStats(sessionDateRange[0].toDate(), sessionDateRange[1].toDate());
    } else {
      fetchSessionStats();
    }
  }, [sessionDateRange]);

  // Auto-refresh removed - users can use refresh buttons on each tab

  // Handler Functions
  const handleEdit = () => {
    form.setFieldsValue({
      name: device.name,
      mac_address: device.mac_address,
      serial_number: device.serial_number,
      model: device.model,
      firmware_version: device.firmware_version,
      customer_id: device.customer_id
    });
    setEditModalVisible(true);
  };

  const handleUpdate = async (values) => {
    try {
      await axios.put(`/api/v1/devices/${id}`, values);
      message.success('Cihaz güncellendi');
      setEditModalVisible(false);
      fetchDeviceData();
    } catch (error) {
      message.error(error.response?.data?.detail || 'Güncelleme başarısız');
    }
  };

  const handleDelete = () => {
    Modal.confirm({
      title: 'Cihazı silmek istediğinizden emin misiniz?',
      content: 'Bu işlem geri alınamaz. Tüm telemetri verileri ve loglar da silinecektir.',
      okText: 'Evet, Sil',
      okType: 'danger',
      cancelText: 'İptal',
      onOk: async () => {
        try {
          await axios.delete(`/api/v1/devices/${id}`);
          message.success('Cihaz silindi');
          navigate('/devices');
        } catch (error) {
          message.error(error.response?.data?.detail || 'Silme işlemi başarısız');
        }
      },
    });
  };

  const getStatusTag = (status) => {
    const statusConfig = {
      online: { color: 'green', icon: <CheckCircleOutlined />, text: 'Çevrimiçi' },
      offline: { color: 'orange', icon: <ClockCircleOutlined />, text: 'Çevrimdışı' },
      error: { color: 'red', icon: <ExclamationCircleOutlined />, text: 'Hata' },
      maintenance: { color: 'blue', icon: <SettingOutlined />, text: 'Bakım' },
    };
    const config = statusConfig[status] || { color: 'default', icon: <InfoCircleOutlined />, text: status };
    return <Tag color={config.color} icon={config.icon}>{config.text}</Tag>;
  };

  const getOperationalStatusTag = (status) => {
    const statusConfig = {
      idle: { color: 'default', icon: <ClockCircleOutlined />, text: 'Boşta' },
      running: { color: 'green', icon: <ThunderboltOutlined />, text: 'Çalışıyor' },
      paused: { color: 'orange', icon: <ClockCircleOutlined />, text: 'Duraklatıldı' },
      error: { color: 'red', icon: <ExclamationCircleOutlined />, text: 'Hata' },
      maintenance: { color: 'blue', icon: <SettingOutlined />, text: 'Bakımda' },
    };
    const config = statusConfig[status] || { color: 'default', icon: <InfoCircleOutlined />, text: status };
    return <Tag color={config.color} icon={config.icon}>{config.text}</Tag>;
  };

  // Reusable date filter renderer
  const renderDateFilter = (dateRange, setDateRange) => (
    <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      <Text strong>Tarih:</Text>
      <RangePicker
        value={dateRange}
        onChange={setDateRange}
        format="YYYY-MM-DD"
        style={{ maxWidth: 280 }}
        size="small"
      />
      <Space size={4} wrap>
        <Button
          size="small"
          type={dateRange && dayjs(dateRange[0]).isSame(dayjs(), 'day') && dayjs(dateRange[1]).isSame(dayjs(), 'day') ? 'primary' : 'default'}
          onClick={() => setDateRange([dayjs().startOf('day'), dayjs().endOf('day')])}
        >
          Bugün
        </Button>
        <Button
          size="small"
          onClick={() => setDateRange([dayjs().subtract(7, 'days').startOf('day'), dayjs().endOf('day')])}
        >
          Son 7 Gün
        </Button>
        <Button
          size="small"
          onClick={() => setDateRange([dayjs().subtract(30, 'days').startOf('day'), dayjs().endOf('day')])}
        >
          Son 30 Gün
        </Button>
        <Button
          size="small"
          type={!dateRange ? 'primary' : 'default'}
          onClick={() => setDateRange(null)}
        >
          Tümü
        </Button>
      </Space>
    </div>
  );

  // Session logs from separate state
  const sessionLogs = sessionLogsRaw;

  // OTA history filtered by date range (frontend filter - backend has no date param)
  const filteredOtaHistory = otaDateRange && otaDateRange.length === 2
    ? otaHistory.filter(record => {
        const otaTime = new Date(record.started_at || record.created_at);
        return otaTime >= otaDateRange[0].toDate() && otaTime <= otaDateRange[1].toDate();
      })
    : otaHistory;

  // Filter session logs by date range (session_start_time bazlı)
  const filteredSessionLogs = sessionDateRange && sessionDateRange.length === 2
    ? sessionLogs.filter(log => {
        const sessionTime = new Date(log.session_start_time || log.timestamp);
        return sessionTime >= sessionDateRange[0].toDate() && sessionTime <= sessionDateRange[1].toDate();
      })
    : sessionLogs;

  // Get latest telemetry
  // Backend desc sıralı gönderiyor: telemetryData[0] = en yeni kayıt
  const latestTelemetry = telemetryData.length > 0 ? telemetryData[0] : null;

  if (loading || !device) {
    return (
      <div style={{ textAlign: 'center', paddingTop: 48 }}>
        <Title level={4}>Yükleniyor...</Title>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Space>
          <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={() => {
              if (fromPage === 'customer' && device?.customer_id) {
                // Müşteri detay sayfasından geldiyse, müşteri detay sayfasına dön
                navigate(`/customers/${device.customer_id}?tab=devices`);
            } else {
                // Diğer durumlarda bir önceki sayfaya dön
                navigate(-1);
            }
            }}
          >
            Geri
          </Button>
          <Title level={2} style={{ margin: 0 }}>
            {device.name}
          </Title>
          {getStatusTag(device.status)}
        </Space>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={() => {
            fetchDeviceData();
            // Her tab kendi tarih filtresiyle yenilensin
            setTelemetryDateRange(prev => prev ? [...prev] : null);
            setErrorDateRange(prev => prev ? [...prev] : null);
            setAllLogsDateRange(prev => prev ? [...prev] : null);
            setSessionDateRange(prev => prev ? [...prev] : null);
            fetchOTAHistory();
          }}>
            Yenile
          </Button>
          {!isCustomer && (
            <>
              <Button icon={<EditOutlined />} onClick={handleEdit}>
                Düzenle
              </Button>
              <Button icon={<DeleteOutlined />} danger onClick={handleDelete}>
                Sil
              </Button>
            </>
          )}
        </Space>
      </div>

      {/* Live Telemetry Summary */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <Space size="large" wrap>
            <span>
              <FireOutlined style={{ color: '#ff4d4f', marginRight: 6 }} />
              <Text style={{ marginRight: 4 }}>Su</Text>
              <Text strong style={{ color: '#ff4d4f', fontSize: 18 }}>
                {latestTelemetry?.water_temperature?.toFixed(1) || '-'}
              </Text>
              <Text> °C</Text>
            </span>
            <span>
              <DashboardOutlined style={{ color: '#1890ff', marginRight: 6 }} />
              <Text style={{ marginRight: 4 }}>Ortam</Text>
              <Text strong style={{ color: '#1890ff', fontSize: 18 }}>
                {latestTelemetry?.ambient_temperature?.toFixed(1) || '-'}
              </Text>
              <Text> °C</Text>
            </span>
            <span>
              <SignalFilled style={{ color: '#52c41a', marginRight: 6 }} />
              <Text style={{ marginRight: 4 }}>Sinyal</Text>
              <Text strong style={{ fontSize: 18 }}>
                {latestTelemetry?.signal_strength || '-'}
              </Text>
              <Text> dBm</Text>
            </span>
            <span>
              <DashboardOutlined style={{ color: '#13c2c2', marginRight: 6 }} />
              <Text style={{ marginRight: 4 }}>Akış</Text>
              <Text strong style={{ fontSize: 18 }}>
                {latestTelemetry?.water_flow || '-'}
              </Text>
              <Text> L/min</Text>
            </span>
            <span>
              <ThunderboltOutlined style={{ color: '#fa8c16', marginRight: 6 }} />
              <Text style={{ marginRight: 4 }}>Toplam Atış</Text>
              <Text strong style={{ fontSize: 18 }}>
                {latestTelemetry?.total_shots ?? '-'}
              </Text>
            </span>
          </Space>
          {latestTelemetry && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {safeGetRelativeTime(latestTelemetry.timestamp)}
            </Text>
          )}
        </div>
      </Card>

      {/* Main Content Tabs */}
      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        {/* Overview Tab */}
        <Tabs.TabPane 
          tab={<span><InfoCircleOutlined />Genel Bakış</span>} 
          key="overview"
        >
          <Row gutter={[16, 16]} align="stretch">
            <Col xs={24} md={isCustomer ? 12 : 8}>
              <Card title="Cihaz Bilgileri" bordered={false} style={{ height: '100%' }}>
                <Descriptions column={1} size="small">
                  <Descriptions.Item label="Cihaz Adı">{device.name}</Descriptions.Item>
                  <Descriptions.Item label="MAC Adresi">
                    <Text code>{device.mac_address}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label="Seri Numarası">{device.serial_number || '-'}</Descriptions.Item>
                  <Descriptions.Item label="Model">{device.model || '-'}</Descriptions.Item>
                  <Descriptions.Item label="ESP32 Firmware">{device.firmware_version || '-'}</Descriptions.Item>
                  <Descriptions.Item label="STM32 Firmware">{device.stm32_firmware_version || '—'}</Descriptions.Item>
                  <Descriptions.Item label="Durum">{getStatusTag(device.status)}</Descriptions.Item>
                  <Descriptions.Item label="Müşteri">
                    {device.customer ? (
                      isCustomer ? (
                        <Text>{device.customer.name}</Text>
                      ) : (
                        <a onClick={() => navigate(`/customers/${device.customer_id}`)}>{device.customer.name}</a>
                      )
                    ) : 'Atanmamış'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Kiralama">
                    {activeRental ? (
                      <Space direction="vertical" size={2}>
                        <Space size={4}>
                          <Tag color={activeRental.billing_type === 'fixed_monthly' ? 'blue' : 'purple'} style={{ margin: 0 }}>
                            {activeRental.billing_type === 'fixed_monthly' ? 'Sabit Aylık' : 'Atış Bazlı'}
                          </Tag>
                          <Text>
                            {activeRental.billing_type === 'fixed_monthly'
                              ? `₺${parseFloat(activeRental.monthly_rate || 0).toFixed(2)}/ay`
                              : `₺${parseFloat(activeRental.unit_price || 0).toFixed(4)}/atış`
                            }
                          </Text>
                        </Space>
                        <Text type="secondary" style={{ fontSize: 11 }}>
                          {safeFormatTimestamp(activeRental.start_date)} ({(() => {
                            const days = Math.floor((new Date() - new Date(activeRental.start_date)) / 86400000);
                            return days < 30 ? `${days} gün` : `${Math.floor(days/30)} ay ${days%30} gün`;
                          })()})
                        </Text>
                        {!isCustomer && (
                          <Button size="small" danger type="link" style={{ padding: 0, height: 'auto', fontSize: 11 }}
                            onClick={async () => {
                              try {
                                await axios.post(`/api/v1/rentals/${activeRental.id}/end?end_date=${encodeURIComponent(new Date().toISOString())}`);
                                message.success('Kiralama sonlandırıldı');
                                fetchDeviceData();
                              } catch (e) { message.error(e.response?.data?.detail || 'Hata'); }
                            }}
                          >Sonlandır</Button>
                        )}
                      </Space>
                    ) : (
                      <Space direction="vertical" size={2}>
                        <Text type="secondary">Kiralama yok</Text>
                        {!isCustomer && (
                          <Button size="small" type="link" style={{ padding: 0, height: 'auto', fontSize: 11 }}
                            onClick={() => navigate('/rentals')}
                          >Kiralama Başlat</Button>
                        )}
                      </Space>
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label="Son Görülme">
                    {device.last_seen ? (
                      <Tooltip title={safeFormatTimestamp(device.last_seen)}>
                        <Text>{safeGetRelativeTime(device.last_seen)}</Text>
                      </Tooltip>
                    ) : 'Hiç görülmedi'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Oluşturulma">
                    {safeFormatTimestamp(device.created_at)}
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            </Col>
            <Col xs={24} md={isCustomer ? 12 : 8}>
              <Card title="Operasyonel Durum" bordered={false} style={{ height: '100%' }}>
                {latestTelemetry ? (
                  <Descriptions column={1} size="small">
                    <Descriptions.Item label="Durum">
                      {getOperationalStatusTag(device.operational_status || latestTelemetry.operational_status)}
                    </Descriptions.Item>
                    <Descriptions.Item label="Açıklama">
                      {device.status_summary || latestTelemetry.status_summary || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Su Sıcaklığı">
                      {latestTelemetry.water_temperature}°C
                    </Descriptions.Item>
                    <Descriptions.Item label="Ortam Sıcaklığı">
                      {latestTelemetry.ambient_temperature}°C
                    </Descriptions.Item>
                    <Descriptions.Item label="Su Akışı">
                      {latestTelemetry.water_flow} L/min
                    </Descriptions.Item>
                    <Descriptions.Item label="Sinyal Gücü">
                      {latestTelemetry.signal_strength} dBm
                    </Descriptions.Item>
                    <Descriptions.Item label="Toplam Atış">
                      {latestTelemetry.total_shots}
                    </Descriptions.Item>
                  </Descriptions>
                ) : (
                  <Empty description="Telemetri verisi yok" />
                )}
              </Card>
            </Col>
            {!isCustomer && (
            <Col xs={24} md={8}>
              {hasPermission(user?.role, 'devices.view_mqtt_credentials') ? (
                <Card
                  title={<span><KeyOutlined /> MQTT Bağlantı</span>}
                  bordered={false}
                  loading={mqttCredentialsLoading}
                  style={{ height: '100%' }}
                >
                  {mqttCredentials ? (
                    <Descriptions column={1} size="small">
                      <Descriptions.Item label="Kullanıcı">
                        <Space size={4}>
                          <Text code style={{ fontSize: 11 }}>{mqttCredentials.mqtt_username}</Text>
                          <Button
                            size="small"
                            type="text"
                            icon={<CopyOutlined />}
                            onClick={() => {
                              navigator.clipboard.writeText(mqttCredentials.mqtt_username);
                              message.success('Kopyalandı');
                            }}
                          />
                        </Space>
                      </Descriptions.Item>
                      <Descriptions.Item label="Şifre">
                        <Space size={4}>
                          <Text code copyable style={{ fontSize: 11 }}>{mqttCredentials.mqtt_password}</Text>
                        </Space>
                      </Descriptions.Item>
                      <Descriptions.Item label="Broker">
                        <Text code style={{ fontSize: 11 }}>{mqttCredentials.mqtt_broker}:{mqttCredentials.mqtt_port}</Text>
                      </Descriptions.Item>
                      <Descriptions.Item label="TLS">
                        <Text type="secondary" style={{ fontSize: 12 }}>Port 8883 (Zorunlu)</Text>
                      </Descriptions.Item>
                      <Descriptions.Item label="Durum">
                        <Tag color={mqttCredentials.mqtt_enabled ? 'green' : 'red'} style={{ margin: 0 }}>
                          {mqttCredentials.mqtt_enabled ? 'Aktif' : 'Devre Dışı'}
                        </Tag>
                      </Descriptions.Item>
                    </Descriptions>
                  ) : (
                    <Alert
                      message="MQTT bilgileri yüklenemedi"
                      type="warning"
                      showIcon
                    />
                  )}
                </Card>
              ) : (
                <Card title="Bağlantı" bordered={false} style={{ height: '100%' }}>
                  <Descriptions column={1} size="small">
                    <Descriptions.Item label="Protokol">MQTT over TLS</Descriptions.Item>
                    <Descriptions.Item label="Port">8883</Descriptions.Item>
                    <Descriptions.Item label="MQTT">
                      <Tag color={device.mqtt_enabled !== false ? 'green' : 'red'} style={{ margin: 0 }}>
                        {device.mqtt_enabled !== false ? 'Aktif' : 'Devre Dışı'}
                      </Tag>
                    </Descriptions.Item>
                  </Descriptions>
                </Card>
              )}
            </Col>
            )}
          </Row>

          {/* Kiralama Geçmişi */}
          {!isCustomer && rentalHistory.length > 0 && (
            <Card size="small" title="Kiralama Geçmişi" style={{ marginTop: 16 }}
              extra={<Button type="link" size="small" onClick={() => navigate('/rentals')}>Tümü</Button>}
            >
              <Table
                dataSource={rentalHistory}
                rowKey="id"
                pagination={false}
                size="small"
                columns={[
                  { title: 'Müşteri', dataIndex: 'customer_name', key: 'customer_name', render: (t, r) => isCustomer ? <Text>{t || `#${r.customer_id}`}</Text> : <a onClick={() => navigate(`/customers/${r.customer_id}`)}>{t || `#${r.customer_id}`}</a> },
                  { title: 'Başlangıç', dataIndex: 'start_date', key: 'start_date', width: 140, render: (d) => d ? safeFormatTimestamp(d) : '-' },
                  { title: 'Bitiş', dataIndex: 'end_date', key: 'end_date', width: 140, render: (d) => d ? safeFormatTimestamp(d) : <Tag color="green">Aktif</Tag> },
                  { title: 'Tip', dataIndex: 'billing_type', key: 'billing_type', width: 100, render: (t) => <Tag color={t === 'fixed_monthly' ? 'blue' : 'purple'}>{t === 'fixed_monthly' ? 'Sabit' : 'Atış'}</Tag> },
                ]}
              />
            </Card>
          )}
        </Tabs.TabPane>

        {/* Telemetry Tab */}
        <Tabs.TabPane
          tab={<span><BarChartOutlined />Telemetri</span>}
          key="telemetry"
        >
              <Card
            title="Sensör Verileri"
            extra={
              <Space>
                <Button
                  size="small"
                  icon={<ReloadOutlined />}
                  onClick={() => {
                    if (telemetryDateRange && telemetryDateRange.length === 2) {
                      fetchTelemetryData(telemetryDateRange[0].toDate(), telemetryDateRange[1].toDate());
                    } else {
                      fetchTelemetryData();
                    }
                  }}
                  loading={logsLoading}
                >
                  Yenile
                </Button>
              </Space>
                }
              >
                {/* Telemetry Date Filter */}
                {renderDateFilter(telemetryDateRange, setTelemetryDateRange)}
                {telemetryData.length > 0 && (
                  <Text type="secondary" style={{ fontSize: 12, marginBottom: 8, display: 'block' }}>
                    {telemetryData.length} kayıt
                  </Text>
                )}
                {telemetryData.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart data={telemetryData.slice().reverse()}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="timestamp" 
                      tickFormatter={(value) => new Date(value).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                      />
                    <YAxis yAxisId="left" />
                    <YAxis yAxisId="right" orientation="right" />
                    <RechartsTooltip 
                          labelFormatter={(value) => safeFormatTimestamp(value)}
                      formatter={(value, name) => {
                        if (name === 'Su Sıcaklığı' || name === 'Ortam Sıcaklığı') return [`${value}°C`, name];
                        if (name === 'Su Akışı') return [`${value} L/min`, name];
                        if (name === 'Sinyal') return [`${value} dBm`, name];
                        return [value, name];
                      }}
                    />
                    <Legend />
                       <Line 
                      yAxisId="left"
                         type="monotone" 
                         dataKey="water_temperature" 
                      stroke="#ff4d4f" 
                         name="Su Sıcaklığı"
                         dot={false}
                      strokeWidth={2}
                       />
                       <Line 
                      yAxisId="left"
                         type="monotone" 
                         dataKey="ambient_temperature" 
                      stroke="#1890ff" 
                         name="Ortam Sıcaklığı"
                         dot={false}
                      strokeWidth={2}
                       />
                       <Line 
                      yAxisId="right"
                         type="monotone" 
                      dataKey="water_flow" 
                      stroke="#52c41a" 
                      name="Su Akışı"
                         dot={false}
                      strokeWidth={2}
                       />
                    </LineChart>
                  </ResponsiveContainer>
                
                {/* Telemetry Table */}
                 <Table
                  dataSource={telemetryData}
                  rowKey={(record) => record.timestamp}
                  pagination={{ pageSize: 20, showSizeChanger: true }}
                  size="small"
                  style={{ marginTop: 16 }}
                   columns={[
                     {
                       title: 'Zaman',
                       dataIndex: 'timestamp',
                       key: 'timestamp',
                      render: (text) => safeFormatTimestamp(text),
                      width: 180
                    },
                    {
                      title: 'Durum',
                      dataIndex: 'operational_status',
                      key: 'operational_status',
                      render: (text) => getOperationalStatusTag(text),
                      width: 120
                    },
                    {
                      title: 'Su Sıcaklığı',
                       dataIndex: 'water_temperature',
                       key: 'water_temperature',
                      render: (text) => `${text}°C`,
                      width: 120
                     },
                     {
                      title: 'Ortam Sıcaklığı',
                       dataIndex: 'ambient_temperature',
                       key: 'ambient_temperature',
                      render: (text) => `${text}°C`,
                      width: 130
                    },
                    {
                      title: 'Su Akışı',
                      dataIndex: 'water_flow',
                      key: 'water_flow',
                      render: (text) => `${text} L/min`,
                      width: 110
                    },
                    {
                      title: 'Sinyal',
                      dataIndex: 'signal_strength',
                      key: 'signal_strength',
                      render: (text) => `${text} dBm`,
                      width: 100
                    },
                    {
                      title: 'Toplam Atış',
                      dataIndex: 'total_shots',
                      key: 'total_shots',
                      width: 110
                    }
                  ]}
                />
              </>
            ) : (
              <Empty description="Telemetri verisi bulunamadı" />
            )}
               </Card>
        </Tabs.TabPane>

        {/* Sessions Tab */}
        <Tabs.TabPane
          tab={
            <span>
              <HistoryOutlined />
              Seanslar
              <Badge count={filteredSessionLogs.length} style={{ marginLeft: 8 }} />
            </span>
          }
          key="sessions"
        >
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            {/* Session Stats Summary */}
            {sessionStats && (
              <Card size="small">
                <Space size="large" wrap>
                  <span>
                    <HistoryOutlined style={{ color: '#1890ff', marginRight: 6 }} />
                    <Text style={{ marginRight: 4 }}>Toplam Seans</Text>
                    <Text strong style={{ fontSize: 18 }}>{sessionStats.total_sessions || 0}</Text>
                  </span>
                  <span>
                    <ThunderboltOutlined style={{ color: '#fa8c16', marginRight: 6 }} />
                    <Text style={{ marginRight: 4 }}>Toplam Atış</Text>
                    <Text strong style={{ fontSize: 18 }}>{sessionStats.total_shots || 0}</Text>
                  </span>
                  <span>
                    <ClockCircleOutlined style={{ color: '#52c41a', marginRight: 6 }} />
                    <Text style={{ marginRight: 4 }}>Ort. Süre</Text>
                    <Text strong style={{ fontSize: 18 }}>{sessionStats.average_duration || 0}</Text>
                    <Text> sn</Text>
                  </span>
                  <span>
                    <FireOutlined style={{ color: '#f5222d', marginRight: 6 }} />
                    <Text style={{ marginRight: 4 }}>Toplam Enerji</Text>
                    <Text strong style={{ fontSize: 18 }}>{(sessionStats.total_joule || 0).toFixed(2)}</Text>
                    <Text> J</Text>
                  </span>
                </Space>
              </Card>
            )}

            {/* Filters */}
            <Card
              title="Cihaz Seansları"
              extra={
                <Space>
                  <Button
                    size="small"
                    icon={<ReloadOutlined />}
                    onClick={() => {
                      fetchSessionLogs(); // Fetch session logs only
                      fetchSessionStats(
                        sessionDateRange?.[0]?.toDate(),
                        sessionDateRange?.[1]?.toDate()
                      );
                    }}
                    loading={logsLoading}
                  >
                    Yenile
                  </Button>
                  <Button
                    size="small"
                    type="primary"
                    icon={<DownloadOutlined />}
                    onClick={handleExportToExcel}
                    disabled={filteredSessionLogs.length === 0}
                  >
                    Excel'e Aktar
                  </Button>
                </Space>
              }
            >
              {/* Date Filter and Quick Buttons */}
              <Space
                direction="vertical"
                size="middle"
                style={{ width: '100%', marginBottom: 16 }}
              >
                <Row gutter={[8, 8]} align="middle">
                  <Col>
                    <Text strong>Tarih Filtresi:</Text>
                  </Col>
                  <Col flex="auto">
                    <RangePicker
                      value={sessionDateRange}
                      onChange={setSessionDateRange}
                      format="YYYY-MM-DD"
                      style={{ width: '100%', maxWidth: 300 }}
                    />
                  </Col>
                </Row>

                <Row gutter={[8, 8]}>
                  <Col>
                    <Text strong>Hızlı Filtre:</Text>
                  </Col>
                  <Col>
                    <Space wrap>
                      <Button
                        size="small"
                        onClick={() => handleQuickFilter('today')}
                        type={
                          sessionDateRange &&
                          dayjs(sessionDateRange[0]).isSame(dayjs(), 'day')
                            ? 'primary'
                            : 'default'
                        }
                      >
                        Bugün
                      </Button>
                      <Button
                        size="small"
                        onClick={() => handleQuickFilter('last7days')}
                      >
                        Son 7 Gün
                      </Button>
                      <Button
                        size="small"
                        onClick={() => handleQuickFilter('last30days')}
                      >
                        Son 30 Gün
                      </Button>
                      <Button
                        size="small"
                        onClick={() => handleQuickFilter('thisMonth')}
                      >
                        Bu Ay
                      </Button>
                      <Button
                        size="small"
                        onClick={() => handleQuickFilter('all')}
                        type={!sessionDateRange ? 'primary' : 'default'}
                      >
                        Tümü
                      </Button>
                    </Space>
                  </Col>
                </Row>
              </Space>

              {/* Sessions Table */}
              <Table
                dataSource={filteredSessionLogs}
                rowKey={(record) => record.timestamp}
                loading={logsLoading}
                pagination={{ pageSize: 20, showSizeChanger: true }}
                size="small"
                columns={[
                  {
                    title: 'Başlangıç',
                    dataIndex: 'session_start_time',
                    key: 'session_start_time',
                    render: (text) => text ? safeFormatTimestamp(text) : '-',
                    width: 180
                  },
                  {
                    title: 'Bitiş',
                    dataIndex: 'session_end_time',
                    key: 'session_end_time',
                    render: (text) => text ? safeFormatTimestamp(text) : '-',
                    width: 180
                  },
                  {
                    title: 'Süre',
                    dataIndex: 'session_duration',
                    key: 'session_duration',
                    render: (text) => text ? `${Math.floor(text / 60)}dk ${text % 60}sn` : '-',
                    width: 100
                  },
                  {
                    title: 'Atış',
                    dataIndex: 'session_shots',
                    key: 'session_shots',
                    width: 80
                  },
                  {
                    title: 'Cinsiyet',
                    dataIndex: 'session_gender',
                    key: 'session_gender',
                    render: formatGender,
                    width: 80
                  },
                  {
                    title: 'Cilt',
                    dataIndex: 'session_skin_type',
                    key: 'session_skin_type',
                    render: formatSkinType,
                    width: 80
                  },
                  {
                    title: 'Kıl',
                    dataIndex: 'session_hair_type',
                    key: 'session_hair_type',
                    render: formatHairType,
                    width: 90
                  },
                  {
                    title: 'Alan',
                    dataIndex: 'session_area',
                    key: 'session_area',
                    render: formatArea,
                    width: 100
                  },
                  {
                    title: 'Joule',
                    dataIndex: 'session_joule',
                    key: 'session_joule',
                    render: (text) => text ? `${text} J` : '-',
                    width: 100
                  },
                  {
                    title: 'Frekans',
                    dataIndex: 'session_hz',
                    key: 'session_hz',
                    render: (text) => text ? `${text} Hz` : '-',
                    width: 100
                  },
                  {
                    title: 'Mesaj',
                    dataIndex: 'message',
                    key: 'message',
                    ellipsis: {
                      showTitle: false
                    },
                    render: (text) => (
                      <Tooltip title={text} placement="topLeft">
                        <span>{text}</span>
                      </Tooltip>
                    )
                  }
                ]}
              />
            </Card>
          </Space>
        </Tabs.TabPane>

        {/* Errors Tab */}
        {!isCustomer && (
        <Tabs.TabPane
          tab={
            <span>
              <BugOutlined />
              Hatalar
              <Badge count={errorLogs.length} style={{ marginLeft: 8 }} status="error" />
            </span>
          }
          key="errors"
        >
          <Card
            title="Hata Logları"
            extra={
                <Button
                  size="small"
                  icon={<ReloadOutlined />}
                  onClick={() => {
                    if (errorDateRange && errorDateRange.length === 2) {
                      fetchErrorLogs(errorDateRange[0].toDate(), errorDateRange[1].toDate());
                    } else {
                      fetchErrorLogs();
                    }
                  }}
                  loading={errorLogsLoading}
                >
                  Yenile
                </Button>
            }
          >
            {renderDateFilter(errorDateRange, setErrorDateRange)}
            <Table
              dataSource={errorLogs}
              rowKey={(record) => record.timestamp}
              loading={errorLogsLoading}
              pagination={{ pageSize: 20, showSizeChanger: true }}
              size="small"
              columns={[
                {
                  title: 'Zaman',
                  dataIndex: 'timestamp',
                  key: 'timestamp',
                  render: (text) => safeFormatTimestamp(text),
                  width: 180
                },
                {
                  title: 'Seviye',
                  dataIndex: 'error_severity',
                  key: 'error_severity',
                  render: (text) => {
                    const colorMap = {
                      critical: 'red',
                      high: 'orange',
                      medium: 'yellow',
                      low: 'blue'
                    };
                    return <Tag color={colorMap[text] || 'default'}>{text || 'unknown'}</Tag>;
                  },
                  width: 100
                },
                {
                  title: 'Hata Kodu',
                  dataIndex: 'error_code',
                  key: 'error_code',
                  width: 120
                },
                {
                  title: 'Hata Mesajı',
                  dataIndex: 'error_message',
                  key: 'error_message',
                  ellipsis: true
                },
                {
                  title: 'Detay',
                  dataIndex: 'message',
                  key: 'message',
                  ellipsis: true
                }
              ]}
            />
          </Card>
        </Tabs.TabPane>
        )}

        {/* OTA Tab - DeviceFirmwareUpdate kayıtları */}
        {!isCustomer && (
        <Tabs.TabPane
          tab={
            <span>
              <RocketOutlined />
              OTA Geçmişi
              <Badge count={filteredOtaHistory.length} style={{ marginLeft: 8 }} />
            </span>
          }
          key="ota"
        >
          <Card
            title="Firmware Güncelleme Geçmişi"
            extra={
              <Button
                size="small"
                icon={<ReloadOutlined />}
                onClick={fetchOTAHistory}
                loading={otaHistoryLoading}
              >
                Yenile
              </Button>
            }
          >
            {renderDateFilter(otaDateRange, setOtaDateRange)}
            <Table
              dataSource={filteredOtaHistory}
              rowKey={(record) => record.id}
              loading={otaHistoryLoading}
              pagination={{ pageSize: 20, showSizeChanger: true }}
              size="small"
              locale={{
                emptyText: (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="Henüz OTA güncellemesi yapılmamış"
                  />
                )
              }}
              columns={[
                {
                  title: 'Tarih',
                  dataIndex: 'started_at',
                  key: 'started_at',
                  render: (text) => text ? safeFormatTimestamp(text) : '-',
                  width: 180
                },
                {
                  title: 'Firmware',
                  dataIndex: 'firmware_version',
                  key: 'firmware_version',
                  render: (version) => version ? <Tag color="blue">{version}</Tag> : '-',
                  width: 120
                },
                {
                  title: 'Durum',
                  dataIndex: 'status',
                  key: 'status',
                  render: (status) => {
                    const statusConfig = {
                      pending: { color: 'orange', icon: <ClockCircleOutlined />, text: 'Bekliyor' },
                      downloading: { color: 'blue', icon: <ThunderboltOutlined />, text: 'İndiriliyor' },
                      installing: { color: 'cyan', icon: <ThunderboltOutlined />, text: 'Yükleniyor' },
                      completed: { color: 'green', icon: <CheckCircleOutlined />, text: 'Tamamlandı' },
                      failed: { color: 'red', icon: <ExclamationCircleOutlined />, text: 'Başarısız' },
                      cancelled: { color: 'default', icon: <ExclamationCircleOutlined />, text: 'İptal' },
                      timeout: { color: 'warning', icon: <WarningOutlined />, text: 'Zaman Aşımı' }
                    };
                    const config = statusConfig[status] || { color: 'default', text: status };
                    return <Tag color={config.color} icon={config.icon}>{config.text}</Tag>;
                  },
                  width: 130
                },
                {
                  title: 'Son Durum',
                  key: 'last_known_state',
                  render: (_, record) => {
                    // Show last known state for pending/timeout records
                    if ((record.status === 'pending' || record.status === 'timeout') && record.last_stage) {
                      const stageMap = {
                        downloading: 'İndiriliyor',
                        verifying: 'Doğrulanıyor',
                        installing: 'Yükleniyor'
                      };
                      const stageName = stageMap[record.last_stage] || record.last_stage;
                      const progress = record.last_progress_percent !== null ? `${record.last_progress_percent}%` : '';

                      return (
                        <Tooltip title={record.last_activity_at ? `Son aktivite: ${safeFormatTimestamp(record.last_activity_at)}` : 'Son aktivite bilinmiyor'}>
                          <Space size={4}>
                            <Text type="secondary">{stageName}</Text>
                            {progress && <Text strong>{progress}</Text>}
                          </Space>
                        </Tooltip>
                      );
                    }
                    return '-';
                  },
                  width: 140
                },
                {
                  title: 'Süre',
                  key: 'duration',
                  render: (_, record) => {
                    if (!record.duration_seconds) return '-';
                    const minutes = Math.floor(record.duration_seconds / 60);
                    const seconds = Math.floor(record.duration_seconds % 60);
                    return `${minutes}dk ${seconds}sn`;
                  },
                  width: 100
                },
                {
                  title: 'Tamamlanma',
                  dataIndex: 'completed_at',
                  key: 'completed_at',
                  render: (text) => text ? safeFormatTimestamp(text) : '-',
                  width: 180
                },
                {
                  title: 'İşlemler',
                  key: 'actions',
                  render: (_, record) => (
                    <Space>
                      {/* Retry butonu - failed ve timeout durumları için */}
                      {(record.status === 'failed' || record.status === 'timeout') && (
                        <Tooltip title="Tekrar dene (Aynı versiyonda ise zorla güncelle seçeneği sunulur)">
                          <Button
                            size="small"
                            icon={<ReloadOutlined />}
                            onClick={() => handleRetryOTAWithConfirm(record)}
                          >
                            Tekrar
                          </Button>
                        </Tooltip>
                      )}
                    </Space>
                  ),
                  width: 100
                }
              ]}
            />
          </Card>
        </Tabs.TabPane>
        )}

        {/* All Logs Tab */}
        {!isCustomer && (
        <Tabs.TabPane
          tab={
            <span>
              <FileTextOutlined />
              Tüm Loglar
              <Badge count={allLogs.length} style={{ marginLeft: 8 }} />
            </span>
          }
          key="logs"
        >
          <Card
            title="Tüm Cihaz Logları"
            extra={
              <Button
                size="small"
                icon={<ReloadOutlined />}
                onClick={() => {
                  if (allLogsDateRange && allLogsDateRange.length === 2) {
                    fetchAllLogs(allLogsDateRange[0].toDate(), allLogsDateRange[1].toDate());
                  } else {
                    fetchAllLogs();
                  }
                }}
                loading={allLogsLoading}
              >
                Yenile
              </Button>
            }
          >
            {renderDateFilter(allLogsDateRange, setAllLogsDateRange)}
            <Table
              dataSource={allLogs}
              rowKey={(record) => record.timestamp + record.log_type}
              loading={allLogsLoading}
              pagination={{ pageSize: 20, showSizeChanger: true, showTotal: (total) => `Toplam ${total} log` }}
              size="small"
              scroll={{ x: 1200 }}
              columns={[
                {
                  title: 'Zaman',
                  dataIndex: 'timestamp',
                  key: 'timestamp',
                  render: (text) => safeFormatTimestamp(text),
                  width: 180,
                  fixed: 'left'
                },
                {
                  title: 'Tip',
                  dataIndex: 'log_type',
                  key: 'log_type',
                  render: (text) => {
                    const typeMap = {
                      error: { color: 'red', icon: <BugOutlined /> },
                      session: { color: 'blue', icon: <HistoryOutlined /> },
                      ota_result: { color: 'purple', icon: <RocketOutlined /> },
                      system_log: { color: 'green', icon: <FileTextOutlined /> },
                      control_response: { color: 'orange', icon: <SettingOutlined /> },
                      config_response: { color: 'cyan', icon: <SettingOutlined /> }
                    };
                    const config = typeMap[text] || { color: 'default', icon: null };
                    return <Tag color={config.color} icon={config.icon}>{text}</Tag>;
                  },
                  width: 150,
                  filters: [
                    { text: 'Error', value: 'error' },
                    { text: 'Session', value: 'session' },
                    { text: 'OTA Result', value: 'ota_result' },
                    { text: 'System Log', value: 'system_log' },
                    { text: 'Control Response', value: 'control_response' },
                    { text: 'Config Response', value: 'config_response' }
                  ],
                  onFilter: (value, record) => record.log_type === value
                },
                {
                  title: 'Seviye',
                  dataIndex: 'log_level',
                  key: 'log_level',
                  render: (text) => {
                    const levelMap = {
                      error: 'red',
                      warning: 'orange',
                      info: 'blue',
                      debug: 'default'
                    };
                    return <Tag color={levelMap[text] || 'default'}>{text}</Tag>;
                  },
                  width: 100
                },
                {
                  title: 'Kaynak',
                  dataIndex: 'source',
                  key: 'source',
                  width: 100
                },
                {
                  title: 'Mesaj',
                  dataIndex: 'message',
                  key: 'message',
                  ellipsis: true
                }
              ]}
            />
          </Card>
        </Tabs.TabPane>
        )}
      </Tabs>

      {/* Edit Modal */}
      <Modal
        title="Cihaz Düzenle"
        open={editModalVisible}
        onOk={() => form.submit()}
        onCancel={() => setEditModalVisible(false)}
        okText="Güncelle"
        cancelText="İptal"
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleUpdate}
        >
          <Form.Item
            name="name"
            label="Cihaz Adı"
            rules={[{ required: true, message: 'Cihaz adı gerekli' }]}
          >
            <Input placeholder="Cihaz adını girin" />
          </Form.Item>
          <Form.Item
            name="mac_address"
            label="MAC Adresi"
            rules={[{ required: true, message: 'MAC adresi gerekli' }]}
          >
            <Input placeholder="AA-BB-CC-DD-EE-FF" />
          </Form.Item>
          <Form.Item
            name="serial_number"
            label="Seri Numarası"
          >
            <Input placeholder="Seri numarasını girin" />
          </Form.Item>
          <Form.Item
            name="model"
            label="Model"
          >
            <Input placeholder="Model bilgisini girin" />
          </Form.Item>
          <Form.Item
            name="firmware_version"
            label="Firmware Versiyonu"
          >
            <Input placeholder="Firmware versiyonunu girin" />
          </Form.Item>
          <Form.Item
            name="customer_id"
            label="Müşteri"
          >
            <Select placeholder="Müşteri seçin" allowClear>
              {customers.map(customer => (
                <Option key={customer.id} value={customer.id}>
                  {customer.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default DeviceDetail;

